import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { FactCheckResult } from "../components/FactCheckResult";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";

export default function CheckPage() {
    const { checkId } = useParams();
    const [result, setResult] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | ok | notfound

    useEffect(() => {
        if (!checkId) return;
        api.get(`/checks/${checkId}`)
            .then((r) => { setResult(r.data); setStatus("ok"); })
            .catch(() => setStatus("notfound"));
    }, [checkId]);

    if (status === "loading") {
        return (
            <div className="max-w-3xl mx-auto px-6 py-20 text-center text-muted-foreground">
                <Loader2 className="h-6 w-6 mx-auto animate-spin" />
            </div>
        );
    }
    if (status === "notfound") {
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="check-notfound">
                <SEO title="Fact-check not found" path={`/check/${checkId}`} noindex />
                <h1 className="font-display text-3xl text-accent mb-3">This fact-check has vanished.</h1>
                <p className="text-muted-foreground mb-6">The link may be invalid or the check was removed.</p>
                <Link to="/">
                    <Button className="rounded-full">
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Real-Check
                    </Button>
                </Link>
            </div>
        );
    }

    const verdictWord = (result.verdict || "").replace(/_/g, " ");
    const title = `${verdictWord.toUpperCase()} — "${(result.claim || "").slice(0, 60)}"`;
    const desc = `${verdictWord} · ${result.confidence || 0}% confidence. ${(result.summary || "").slice(0, 140)}`;

    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14" data-testid="check-page">
            <SEO
                title={title}
                description={desc}
                path={`/check/${result.check_id}`}
                ogImage={`https://real-check.org/api/share/card/${result.check_id}.png`}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "ClaimReview",
                    "claimReviewed": (result.claim || "").slice(0, 200),
                    "reviewRating": {
                        "@type": "Rating",
                        "ratingValue": ratingFromVerdict(result.verdict),
                        "bestRating": "5",
                        "worstRating": "1",
                        "alternateName": verdictWord,
                    },
                    "author": { "@type": "Organization", "name": "Real-Check.org" },
                    "url": `https://real-check.org/check/${result.check_id}`,
                }}
            />
            <div className="mb-4">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors">
                    <ArrowLeft className="h-4 w-4" /> All fact-checks
                </Link>
            </div>
            <FactCheckResult result={result} />
        </div>
    );
}

function ratingFromVerdict(v) {
    switch (v) {
        case "true":         return "5";
        case "safe":         return "5";
        case "mostly_true":  return "4";
        case "likely_safe":  return "4";
        case "misleading":   return "2";
        case "suspicious":   return "2";
        case "false":        return "1";
        case "scam":         return "1";
        default:             return "3";
    }
}
